/**
 * Overview: the support board in figures (spec docs/superpowers/specs/2026-10-25-overview-design.md).
 * Tickets right now, the three newest, 14 days of opened and closed, and the team with the day's
 * counts. AI spend lives at Settings, AI spending; the plumbing in the strip's Systems menu. Reads once a minute and after any ticket change.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { DayBars } from '../components/overview/day-bars';
import { Figures } from '../components/overview/figures';
import { RecentTickets } from '../components/overview/recent-tickets';
import { TeamTable } from '../components/overview/team-table';
import { Section } from '../components/ui/section';
import { useProvideAssistantContext } from '../lib/assistant-context';
import { useLive } from '../lib/live';
import { sharedMax } from '../lib/overview-copy';
import { overviewQuery } from '../lib/queries';
import { describeRpcError } from '../lib/rpc';
import { ago } from '../lib/ticket-facts';

export const Route = createFileRoute('/overview')({
  loader: ({ context }) => context.queryClient.query(overviewQuery()),
  component: Overview,
});

function Overview() {
  useProvideAssistantContext(null);
  const q = useQuery(overviewQuery());
  const live = useLive();
  const o = q.data;
  return (
    <div className="mx-auto w-full max-w-[1120px] px-6 pt-4 pb-12">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-title font-medium">Overview</h1>
        {o && <span className="text-hint text-ink-3">{q.isFetching ? 'Updating' : `As of ${ago(o.asOf)}`}</span>}
      </header>
      {q.isError && <p className="mb-4 text-body text-alert">{describeRpcError(q.error)}</p>}
      {!o ? (
        <p className="text-body text-ink-3">Adding it up.</p>
      ) : (
        <div className="flex flex-col gap-6">
          <Figures t={o.tickets} />
          <RecentTickets recent={o.recent} />
          <Section title="Last 14 days">
            <div className="grid gap-6 px-4 py-4 @[860px]/main:grid-cols-2">
              <DayBars title="Opened" days={o.days.map((d) => ({ day: d.day, value: d.opened }))} max={sharedMax(o.days)} unit="tickets" />
              <DayBars title="Resolved" days={o.days.map((d) => ({ day: d.day, value: d.closed }))} max={sharedMax(o.days)} unit="tickets" />
            </div>
          </Section>
          <TeamTable team={live.team} people={o.people} />
        </div>
      )}
    </div>
  );
}
